import { memo } from 'react';
import { Box, Button, Divider, Paper, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InvestmentFinancialMetricsDTO } from '@inversor-hub/types';
import { formatCurrency, formatDate, formatPercentage } from '@/utils/misc';
import { cardSurface } from '../shared/surfaces';

interface PositionCardProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
  canEdit: boolean;
  onManageValuations: () => void;
}

const Stat = ({
  label,
  help,
  value,
  color,
  dotColor,
}: {
  label: string;
  help?: string;
  value: string;
  color?: string;
  dotColor?: string;
}) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      flexDirection: { xs: 'row', sm: 'column' },
      alignItems: { xs: 'baseline', sm: 'stretch' },
      justifyContent: 'space-between',
      gap: 1,
    }}
  >
    <Box display="flex" alignItems="center" gap={0.75}>
      {dotColor && value !== '—' ? (
        <Box
          aria-hidden
          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }}
        />
      ) : null}
      <Typography variant="overline" color="text.secondary" lineHeight={1.4} noWrap>
        {label}
        {help ? (
          <Tooltip title={help} arrow>
            <InfoOutlinedIcon
              fontSize="inherit"
              sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }}
            />
          </Tooltip>
        ) : null}
      </Typography>
    </Box>
    <Typography variant="h6" fontWeight={700} color={value === '—' ? 'text.disabled' : color}>
      {value}
    </Typography>
  </Box>
);

export const PositionCard = memo(
  ({ metrics, scaleAmount, canEdit, onManageValuations }: PositionCardProps) => {
    const { valuation, financing, equityMetrics } = metrics;
    const marketValue = valuation ? scaleAmount(valuation.amount) : null;
    const debt = financing ? scaleAmount(financing.outstandingBalance) : null;
    const equity = equityMetrics.currentEquity.value;
    const ltv =
      financing && valuation && valuation.amount > 0
        ? financing.outstandingBalance / valuation.amount
        : null;
    const debtShare =
      marketValue !== null && debt !== null && marketValue > 0
        ? Math.min(Math.max(debt / marketValue, 0), 1)
        : null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...cardSurface,
        }}
      >
        <Typography variant="overline" color="text.secondary" lineHeight={1.4}>
          Tu posición hoy
        </Typography>

        <Typography
          variant="h3"
          fontWeight={700}
          color={marketValue === null ? 'text.disabled' : undefined}
          sx={{ fontSize: { xs: '2rem', md: '2.25rem' }, mt: 0.5 }}
        >
          {marketValue === null ? '—' : formatCurrency(marketValue)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {valuation
            ? `Valor de mercado · ${formatDate(valuation.valuationDate)} · ${valuation.source}`
            : 'Añade una valoración para calcular equity, LTV y rentabilidad sobre valor'}
        </Typography>

        {debtShare !== null ? (
          <Box
            sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mt: 2.5 }}
            role="img"
            aria-label={`Deuda ${formatPercentage(debtShare)} del valor de mercado, el resto es tu equity`}
          >
            <Box sx={{ width: `${debtShare * 100}%`, bgcolor: 'grey.300' }} />
            <Box sx={{ flex: 1, bgcolor: 'primary.main' }} />
          </Box>
        ) : null}

        <Divider sx={{ my: 2.5 }} />

        <Box
          sx={{
            mt: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 1.25, sm: 2 },
          }}
        >
          <Stat
            label="Deuda"
            help="Capital pendiente estimado con el cuadro de amortización de la ficha."
            value={debt === null ? '—' : formatCurrency(debt)}
            dotColor="grey.300"
          />
          <Stat
            label="Tu equity"
            help="Valor de mercado menos el capital pendiente: lo que quedaría si vendieras hoy, antes de impuestos y gastos de venta."
            value={equity === null ? '—' : formatCurrency(scaleAmount(equity))}
            color="primary.main"
            dotColor="primary.main"
          />
          <Stat
            label="LTV"
            help="Capital pendiente sobre el valor actual de mercado."
            value={ltv === null ? '—' : formatPercentage(ltv)}
          />
        </Box>

        {canEdit ? (
          <Box sx={{ pt: 1.5 }}>
            <Button size="small" variant="text" sx={{ px: 0 }} onClick={onManageValuations}>
              {valuation ? 'Actualizar valoración' : 'Añadir valoración'}
            </Button>
          </Box>
        ) : null}
      </Paper>
    );
  }
);

PositionCard.displayName = 'PositionCard';
